import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  ICustomMedication,
  ISaveCustomMedicationRequest,
} from "../../../@types/admin/AdminMedication";
import { customMedicationsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import { CUSTOM_MEDICATIONS_QUERY_KEY } from "../../queries/admin/customMedications";

function invalidateCustomMedications(
  queryClient: ReturnType<typeof useQueryClient>,
  hospitalGroupId: number
) {
  queryClient.invalidateQueries({
    queryKey: [CUSTOM_MEDICATIONS_QUERY_KEY, hospitalGroupId],
  });
}

/** Adds a custom medication to the hospital (EN-5240). */
export function useCreateCustomMedication() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ICustomMedication,
    Error,
    { hospitalGroupId: number; request: ISaveCustomMedicationRequest }
  >({
    mutationFn: async ({ hospitalGroupId, request }) =>
      ensureOk(
        await customMedicationsService.createCustomMedication(
          hospitalGroupId,
          request
        )
      ),
    onSuccess: (_data, { hospitalGroupId }) =>
      invalidateCustomMedications(queryClient, hospitalGroupId),
  });

  return {
    createCustomMedication: mutateAsync,
    isCreatingCustomMedication: isPending,
  };
}

/**
 * Replaces a custom medication's texts. Patients who already added it see the
 * new name on their list, in their app language; their history keeps the name
 * recorded at each change.
 */
export function useUpdateCustomMedication() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ICustomMedication,
    Error,
    {
      hospitalGroupId: number;
      id: string;
      request: ISaveCustomMedicationRequest;
    }
  >({
    mutationFn: async ({ hospitalGroupId, id, request }) =>
      ensureOk(
        await customMedicationsService.updateCustomMedication(
          hospitalGroupId,
          id,
          request
        )
      ),
    onSuccess: (_data, { hospitalGroupId }) =>
      invalidateCustomMedications(queryClient, hospitalGroupId),
  });

  return {
    updateCustomMedication: mutateAsync,
    isUpdatingCustomMedication: isPending,
  };
}

/** Stops offering a custom medication to patients; it is kept, not deleted. */
export function useDeactivateCustomMedication() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { hospitalGroupId: number; id: string }
  >({
    mutationFn: async ({ hospitalGroupId, id }) =>
      ensureOk(
        await customMedicationsService.deactivateCustomMedication(
          hospitalGroupId,
          id
        )
      ),
    onSuccess: (_data, { hospitalGroupId }) =>
      invalidateCustomMedications(queryClient, hospitalGroupId),
  });

  return {
    deactivateCustomMedication: mutateAsync,
    isDeactivatingCustomMedication: isPending,
  };
}

/** Offers a deactivated custom medication to patients again. */
export function useReactivateCustomMedication() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ICustomMedication,
    Error,
    { hospitalGroupId: number; id: string }
  >({
    mutationFn: async ({ hospitalGroupId, id }) =>
      ensureOk(
        await customMedicationsService.reactivateCustomMedication(
          hospitalGroupId,
          id
        )
      ),
    onSuccess: (_data, { hospitalGroupId }) =>
      invalidateCustomMedications(queryClient, hospitalGroupId),
  });

  return {
    reactivateCustomMedication: mutateAsync,
    isReactivatingCustomMedication: isPending,
  };
}
